import type {
  ExpandedSelectionListsQuery,
  RoleSelectionListQuery,
  SelectionListsQuery,
  SkillTaxonomySecondarySelectionListQuery,
} from '@revelio/data-access';
import type { SelectionListIdNames } from '@revelio/iso-utility';

// Base types from GraphQL queries
type StandardSelectionLists = Omit<
  NonNullable<SelectionListsQuery['selectionList']>,
  '__typename'
>;

type ExpandedSelectionLists = Omit<
  NonNullable<ExpandedSelectionListsQuery['selectionList']>,
  '__typename'
>;

export type GqlSelectionLists = StandardSelectionLists & ExpandedSelectionLists;

export type RoleSelectionLists = Omit<
  NonNullable<RoleSelectionListQuery['selectionList']>,
  '__typename'
>;

export type SkillTaxonomySelectionLists = Omit<
  NonNullable<SkillTaxonomySecondarySelectionListQuery['selectionList']>,
  '__typename'
>;

export type AllSelectionLists = GqlSelectionLists & RoleSelectionLists;

export type AllSelectionListsWithSkillTaxonomy = AllSelectionLists &
  SkillTaxonomySelectionLists;

// Selection list keys
export type StandardSelectionListKey = keyof StandardSelectionLists;
export type GqlSelectionListKey = keyof GqlSelectionLists;
export type RoleSelectionListKey = keyof RoleSelectionLists;
export type SkillTaxonomySelectionListKey = keyof SkillTaxonomySelectionLists;

export type SelectionListKey = GqlSelectionListKey | RoleSelectionListKey;
export type AllSelectionListsWithSkillTaxonomyKey =
  | SelectionListKey
  | SkillTaxonomySelectionListKey;

// Selection list items and state
export type SelectionListItem<T extends SelectionListKey = SelectionListKey> =
  NonNullable<NonNullable<AllSelectionLists[T]>[number]>;

export type SelectionList<T extends SelectionListKey> = SelectionListItem<T>[];

type RemoveNullFromProps<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

export type NormalizedSelectionListItem<
  T extends SelectionListKey = SelectionListKey,
> = Omit<RemoveNullFromProps<SelectionListItem<T>>, 'id'> & {
  id: string;
  [key: string]: unknown;
};

export type SelectionListItems<T extends SelectionListKey = SelectionListKey> =
  {
    id: SelectionListIdNames;
    value: NormalizedSelectionListItem<T>[];
  };

export type SelectionListState<T extends SelectionListKey = SelectionListKey> =
  Partial<{ [key in T]: SelectionList<key> }>;

// Key definitions
const standardGqlSelectionListKeysRecord: Record<
  StandardSelectionListKey,
  true
> = {
  region: true,
  country: true,
  state: true,
  msa: true,
  city: true,
  rics_k10: true,
  rics_k50: true,
  rics_k400: true,
  company: true,
  seniority: true,
  ethnicity: true,
  gender: true,
  highest_degree: true,
  skill_k75: true,
  skill_k700: true,
  skill_k3000: true,
} as Record<GqlSelectionListKey, true>;

export const standardGqlSelectionListKeys = Object.keys(
  standardGqlSelectionListKeysRecord
) as StandardSelectionListKey[];

const gqlSelectionListKeysRecord: Record<GqlSelectionListKey, true> = {
  ...standardGqlSelectionListKeysRecord,
  state: true,
  metro_area: true,
  skill_k25: true,
  skill_k50: true,
  skill_k2500: true,
  skill_mapped: true,
};

export const gqlSelectionListKeys = Object.keys(
  gqlSelectionListKeysRecord
) as GqlSelectionListKey[];

const roleSelectionListKeysRecord: Record<RoleSelectionListKey, true> = {
  role_k7: true,
  role_k50: true,
  role_k150: true,
  role_k500: true,
  role_k1500: true,
  role_k5000: true,
  role_k15000: true,
};

export const roleSelectionListKeys = Object.keys(
  roleSelectionListKeysRecord
) as RoleSelectionListKey[];

export const SKILL_TAXONOMY_SELECTION_LIST_KEYS: readonly SkillTaxonomySelectionListKey[] =
  ['skill_0', 'skill_1', 'skill_2', 'skill_3'] as const;
